import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, apiError, BACKEND_URL } from "@/lib/api";
import { money } from "@/lib/pricing";
import { isNewOrder } from "@/lib/orderDetails";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { OrdersList } from "@/components/admin/OrdersList";
import { AdminCalendar } from "@/components/admin/AdminCalendar";
import { SubmissionDialog } from "@/components/admin/SubmissionDialog";
import { WEDDING_FIELDS, CONTACT_FIELDS } from "@/lib/forms";
import { CalendarX2, Plus, RefreshCw, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { id: "orders", label: "Orders" },
  { id: "calendar", label: "Calendar" },
  { id: "dates", label: "Blocked dates" },
  { id: "weddings", label: "Weddings" },
  { id: "messages", label: "Messages" },
];

export default function Admin() {
  const { user, checked } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [blocked, setBlocked] = useState([]);
  const [weddings, setWeddings] = useState([]);
  const [messages, setMessages] = useState([]);
  const [newDate, setNewDate] = useState("");
  const [tab, setTab] = useState("orders");
  const [updatedAt, setUpdatedAt] = useState(null);
  const [openSub, setOpenSub] = useState(null);
  const seen = useRef(null);

  const loadOrders = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/orders");
      const fresh = data.filter(isNewOrder);
      if (seen.current) {
        fresh.filter((o) => !seen.current.has(o.id)).forEach((o) => toast.success(`New order from ${o.customer_name} · ${money(o.total)}`, { duration: 8000 }));
      }
      seen.current = new Set(fresh.map((o) => o.id));
      setOrders(data);
      setUpdatedAt(new Date());
    } catch {}
  }, []);

  useEffect(() => {
    if (checked && !user) { navigate("/login", { state: { next: "/admin" } }); return; }
    if (checked && user && user.role !== "admin") { navigate("/"); return; }
    if (user?.role !== "admin") return;
    loadOrders();
    api.get("/availability").then(({ data }) => setBlocked(data.blocked_dates || [])).catch(() => {});
    api.get("/admin/wedding-inquiries").then(({ data }) => setWeddings(data)).catch(() => {});
    api.get("/admin/contact-messages").then(({ data }) => setMessages(data)).catch(() => {});
    const t = setInterval(loadOrders, 30000);
    return () => clearInterval(t);
  }, [checked, user, navigate, loadOrders]);

  const toggleBlock = async (dateStr, block) => {
    try {
      const { data } = await api.post("/admin/blocked-dates", { date: dateStr, blocked: block });
      setBlocked(data.blocked_dates);
      if (block) setNewDate("");
      toast.success(block ? "Date blocked" : "Date opened");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const paid = orders.filter((o) => o.payment_status !== "pending");
  const now = new Date();
  const monthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const monthlySales = paid.filter((o) => { const d = new Date(o.created_at); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}` === monthKey; }).reduce((s, o) => s + (o.net_paid || 0), 0);
  const monthName = now.toLocaleString("en-CA", { month: "long" });
  const newCount = orders.filter(isNewOrder).length;
  const active = paid.filter((o) => !["placed", "delivered"].includes(o.status)).length;

  return (
    <div className="App min-h-screen bg-sugar-bg"><Navbar />
      <div className="mx-auto max-w-6xl px-5 py-8 lg:px-8">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="font-display text-3xl font-extrabold text-sugar-ink">Bakery dashboard</h1>
            <p className="mt-1 text-sm text-sugar-ink2">Every order in one place. Updates automatically every 30 seconds.</p>
          </div>
          <button data-testid="admin-refresh" onClick={loadOrders} className="inline-flex items-center gap-1.5 rounded-full border border-sugar-border bg-white px-3 py-1.5 text-xs font-bold text-sugar-ink2 hover:text-sugar-pink">
            <RefreshCw size={13} /> {updatedAt ? `Updated ${updatedAt.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}` : "Refresh"}
          </button>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[{ l: "New orders", v: newCount, hi: newCount > 0, id: "new" }, { l: "In progress", v: active, id: "active" }, { l: "Paid orders", v: paid.length, id: "paid" }, { l: `${monthName} sales`, v: money(monthlySales), id: "monthly-sales" }].map((s) => (
            <div key={s.l} data-testid={`admin-stat-${s.id}`} className={`rounded-2xl border p-4 ${s.hi ? "border-yellow-400 bg-yellow-300" : "border-sugar-border bg-white"}`}>
              <div className={`text-[10px] font-bold uppercase tracking-widest ${s.hi ? "text-sugar-ink" : "text-sugar-pink"}`}>{s.l}</div>
              <div className="mt-1 font-display text-2xl font-extrabold text-sugar-ink">{s.v}</div>
            </div>
          ))}
        </div>

        <div className="no-scrollbar mt-7 flex gap-2 overflow-x-auto">
          {TABS.map((t) => (
            <button key={t.id} data-testid={`admin-tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`shrink-0 rounded-full px-5 py-2 text-sm font-bold ${tab === t.id ? "bg-sugar-pink text-white" : "border border-sugar-border bg-white text-sugar-ink2"}`}>
              {t.label}{t.id === "orders" && newCount ? ` · ${newCount} new` : ""}{t.id === "weddings" && weddings.length ? ` (${weddings.length})` : ""}{t.id === "messages" && messages.length ? ` (${messages.length})` : ""}
            </button>
          ))}
        </div>

        {tab === "orders" && <OrdersList orders={orders} />}
        {tab === "calendar" && <AdminCalendar orders={orders} blocked={blocked} onToggleBlock={toggleBlock} />}

        {tab === "dates" && (
          <div className="mt-6 max-w-lg">
            <div className="rounded-2xl border border-sugar-border bg-white p-6">
              <h3 className="font-display text-lg font-bold text-sugar-ink">Block a pre-order date</h3>
              <p className="mt-1 text-sm text-sugar-ink2">Blocked dates are greyed out in the customer calendar.</p>
              <div className="mt-4 flex gap-2">
                <Input data-testid="admin-block-date-input" type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)} className="rounded-xl border-sugar-border" />
                <Button data-testid="admin-block-date-btn" disabled={!newDate} onClick={() => toggleBlock(newDate, true)} className="rounded-full bg-sugar-pink font-bold text-white hover:bg-sugar-pinkhover"><Plus size={16} className="mr-1" />Block</Button>
              </div>
              <div className="mt-6 space-y-2">
                {[...blocked].sort().map((d) => (
                  <div key={d} data-testid={`admin-blocked-${d}`} className="flex items-center justify-between rounded-xl bg-sugar-cream px-4 py-2.5">
                    <span className="flex items-center gap-2 text-sm font-semibold text-sugar-ink"><CalendarX2 size={15} className="text-sugar-pink" /> {d}</span>
                    <button data-testid={`admin-unblock-${d}`} onClick={() => toggleBlock(d, false)} className="text-xs font-bold text-sugar-pink hover:underline">Open up</button>
                  </div>
                ))}
                {blocked.length === 0 && <p className="text-sm text-sugar-ink2">No blocked dates.</p>}
              </div>
            </div>
          </div>
        )}

        {tab === "weddings" && (
          <div className="mt-6 space-y-3">
            {weddings.length === 0 && <p className="py-10 text-center text-sugar-ink2">No wedding inquiries yet.</p>}
            {weddings.map((w) => (
              <button key={w.id} data-testid={`admin-wedding-${w.id}`} onClick={() => setOpenSub({ entry: w, fields: WEDDING_FIELDS, title: `Wedding inquiry · ${w.name}` })}
                className="flex w-full items-center gap-4 rounded-2xl border border-sugar-border bg-white p-5 text-left transition-transform hover:-translate-y-0.5 hover:border-sugar-pink/60">
                <div className="min-w-0 flex-1">
                  <div className="font-display text-lg font-bold text-sugar-ink">{w.name} · {w.tiers || "—"}</div>
                  <p className="text-xs text-sugar-ink2">{w.email} · {w.phone} · Date: {w.event_date || "—"}</p>
                  <p className="mt-1 text-xs font-bold text-sugar-pink">View full submission</p>
                </div>
                {w.inspo_url && <img src={`${BACKEND_URL}${w.inspo_url}`} alt="inspo" className="h-16 w-16 rounded-xl border border-sugar-border object-cover" />}
                <ChevronRight size={18} className="text-sugar-muted" />
              </button>
            ))}
          </div>
        )}

        {tab === "messages" && (
          <div className="mt-6 space-y-3">
            {messages.length === 0 && <p className="py-10 text-center text-sugar-ink2">No messages yet.</p>}
            {messages.map((m) => (
              <button key={m.id} data-testid={`admin-message-${m.id}`} onClick={() => setOpenSub({ entry: m, fields: CONTACT_FIELDS, title: `Message · ${m.name}` })}
                className="flex w-full items-center gap-4 rounded-2xl border border-sugar-border bg-white p-5 text-left transition-transform hover:-translate-y-0.5 hover:border-sugar-pink/60">
                <div className="min-w-0 flex-1">
                  <div className="font-display text-lg font-bold text-sugar-ink">{m.name}</div>
                  <p className="text-xs text-sugar-ink2">{m.email}{m.phone ? ` · ${m.phone}` : ""}</p>
                  <p className="mt-1 line-clamp-1 text-sm text-sugar-ink">“{m.message}”</p>
                  <p className="mt-1 text-xs font-bold text-sugar-pink">View full submission</p>
                </div>
                <ChevronRight size={18} className="text-sugar-muted" />
              </button>
            ))}
          </div>
        )}
        <SubmissionDialog entry={openSub?.entry} fields={openSub?.fields || []} title={openSub?.title || ""} onClose={() => setOpenSub(null)} />
      </div>
    </div>
  );
}
